import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { cardVariants } from './card.variants.ts'

/**
 * Card is the standard box of the design system. It has no slots — compose content freely inside it.
 *
 * `interactive` is explicit rather than inferred from `render`: `render` swaps the element for any
 * reason, including semantics. When interactive, it requires `render` to provide an accessible
 * interactive element.
 *
 * @summary Standard card box (background / border / elevation / padding / interactive).
 */
function CardRoot(props: CardRoot.Props) {
  const { className, background, border, elevation, padding, interactive, render, ref, ...rest } = props
  if (interactive && render === undefined && import.meta.env.DEV) {
    throw new Error('Card.Root with interactive requires render to provide an accessible interactive element.')
  }
  return useRender({
    render,
    ref,
    defaultTagName: 'div',
    props: {
      'data-slot': 'card',
      className: cn(cardVariants({ background, border, elevation, padding, interactive, className })),
      ...rest,
    },
  })
}
namespace CardRoot {
  type Variants = NonNullable<VariantProps<typeof cardVariants>>
  export type Props = useRender.ComponentProps<'div'> & {
    /** Background fill. `'none'` leaves it to the caller. @default 'normal' */
    background?: NonNullable<Variants['background']> | undefined
    /** Stroke around the box. @default 'basic' */
    border?: NonNullable<Variants['border']> | undefined
    /**
     * Resting shadow depth. Rises one step on hover when `interactive`, except at `'none'`, which
     * has no compound entry and so stays flat.
     * @default 'low'
     */
    elevation?: NonNullable<Variants['elevation']> | undefined
    /** Inner padding. `'none'` for edge-to-edge content that pads its own parts. @default 'card' */
    padding?: NonNullable<Variants['padding']> | undefined
    /** Adds cursor, hover and focus styles. Requires `render` with an accessible interactive element. @default false */
    interactive?: boolean | undefined
  }
}

const Card = {
  Root: CardRoot,
}

namespace Card {
  export namespace Root {
    export type Props = CardRoot.Props
  }
}

export { Card }
